import { Component, OnInit } from '@angular/core';
import { ModuleService } from '../../Services/module.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '../../../../../Services';
import { ModuleContent, Field, AutoFillDefinition, Step, DefaultField } from '../../Models';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { IFeedConfiguration } from '../../Services/IFeedConfiguration';
import { FeedService } from '../../Services/feed.service';
import { ValueIsProperty } from '../../Models/ModuleDefinitions/ValueIsProperty';
import { Patch } from '../../Models/Patch';
import { BasicField } from '../../Models/ModuleDefinitions/basic-field';
import { IAutoFillFieldMapping } from '../../Models/ModuleDefinitions/auto-fill-field-mapping';
import { AutoFillDefinitionBuilder } from '../../builders/auto-fill-definition.builder';
import { IPreviewField } from '../../Models/ModuleDefinitions/preview-field';
import { ProcessType } from '../../Models/configuration-options/process-type';
import { SourceType } from '../../Models/configuration-options/source-type';
import { IFeedConfigurationDataField } from '../../Services/IFeedConfigurationDataField';
import { BasicControlFieldMapping } from '../../Models/ModuleDefinitions/BasicControlFieldMapping';
import { IModuleReturnFieldConfig } from '@app/Models/IModuleReturnFieldConfig';
import { MatToolbar } from '@angular/material/toolbar';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { KeyValuePipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ProcessTypeConfigOptionComponent } from '../configuration-options/process-type/process-type.component';
import { ServerSideLookupOptions } from '../configuration-options/server-side-lookup-options/server-side-lookup-options.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AutofillFieldMappingComponent } from '../autofill-field-mapping/autofill-field-mapping.component';
import { MatCheckbox } from '@angular/material/checkbox';

const GUID_LENGTH = 36;


enum MatchType {
    Fuzzy = 'fuzzy',
    Exact = 'exact'
};

@Component({
    selector: 'aw-lookup-configuration-field-component',
    templateUrl: 'aw-lookup-configuration-field.component.html',
    styleUrls: ['aw-lookup-configuration-field.component.less'],
    imports: [MatToolbar, MatCard, MatCardTitle, MatCardContent, MatRadioGroup, FormsModule, MatRadioButton, MatSelect, MatOption, MatFormField, MatLabel, MatInput, MatProgressBar, ProcessTypeConfigOptionComponent, ServerSideLookupOptions, MatIconButton, MatIcon, MatCardActions, MatButton, AutofillFieldMappingComponent, MatCheckbox, KeyValuePipe]
})
export class AwLookupConfigurationFieldComponent implements OnInit {
    SourceType = SourceType;
    MatchType = MatchType;
    DefaultField = DefaultField;

    moduleId: string;
    stepId: string;
    version: number;
    fieldId: string;
    getMultiFields: boolean;

    module: ModuleContent;
    otherFields: Field[];
    field: Field;
    feeds: IFeedConfiguration[];

    lookupFieldsLoading: boolean;
    lookupFields: BasicField[];

    sourceType: SourceType;
    processType: ProcessType = ProcessType.client;
    matchType: string = MatchType.Fuzzy;
    defaultField: string = DefaultField.None;
    sourceFeed?: IFeedConfiguration;
    sourceUdqId: string;
    sourceUdqModuleId: string;
    primaryField?: BasicField;
    previewFields: IPreviewField[] = [];
    autoFillFieldMappings: IAutoFillFieldMapping[] = [];
    enabledWhenField?: Field;
    enabledWhenValue: string;
    failValidationOnFailedLookup = false;
    publishAnywayOnFailedLookup = false;
    allowMappingAfterFirstSave = false;

    public isEnabledOffline = false;

    valid: boolean;

    constructor(
        private router: Router,
        private moduleService: ModuleService,
        private activatedRoute: ActivatedRoute,
        private tenantService: TenantService,
        private feedService: FeedService,
        public snackBar: MatSnackBar
    ) { }

    async ngOnInit(): Promise<void> {
        const queryParameters = this.activatedRoute.snapshot.queryParams;
        const routeParameters = this.activatedRoute.snapshot.params;

        this.moduleId = routeParameters.moduleId;
        this.stepId = queryParameters.stepId;
        this.getMultiFields = queryParameters.getMultiFields === 'true';
        this.version = +routeParameters.version;
        this.fieldId = routeParameters.fieldId;

        this.module = await this.moduleService
            .loadModuleContent(
                this.tenantService.selectedTenant,
                this.moduleId,
                this.version
        );

        let fields: Field[];

        const step: Step = this.module
            .Steps
            .find(x => x.StepId === this.stepId);

        fields = this.getMultiFields ?
            fields = step.Fields.find(y => y.IsMulti === true).MultiFields
            : step.Fields;

        this.otherFields = fields.filter(y => y.FieldId !== this.fieldId);
        this.field = fields.filter(y => y.FieldId === this.fieldId)[0];

        this.feeds = await this.fetchFeeds();

        if (this.field.AutoFillDefinition) {
            await this.loadFieldAutoFillDefinition(this.field.AutoFillDefinition);
        }

        this.validate();
    }

    async onSourceTypeChange(): Promise<void> {
        switch (this.sourceType) {
            case SourceType.Feed:
                this.onSourceFeedChange();
                break;

            case SourceType.Udq:
                await this.onSourceUdqIdChange();
                break;
        }
    }

    onSourceFeedChange(): void {
        this.clearFieldDetails();

        if (this.sourceFeed) {
            this.lookupFieldsLoading = false;
            this.lookupFields = this.sourceFeed.DataFields.map(dataField => ({
                name: dataField.Name,
                dataType: dataField.DataType,
                mappings: this.generateFeedFieldMappings(dataField)
            }));
            this.sourceUdqModuleId = null;
        }
    }

    generateFeedFieldMappings(dataField: IFeedConfigurationDataField): BasicControlFieldMapping[] {
        if(dataField.DataType !== 'date'){
            return [];
        }

        const baseDateMappings = [{
            controlPropertyName: 'Date1',
            modelPropertyName: dataField.Name + 'Date'
        },
        {
            controlPropertyName: 'TimeZoneOffset1',
            modelPropertyName: dataField.Name + 'TimeZoneOffset'
        }];

        if(dataField.FormatString?.includes('HH:mm')){
            return baseDateMappings.concat([{
                controlPropertyName: 'Time1',
                modelPropertyName: dataField.Name + 'Time'
            },
            {
                controlPropertyName: 'DateTimeUtc1',
                modelPropertyName: dataField.Name + 'DateTimeUtc'
            }]);
        }
        return baseDateMappings;
    }

    async onSourceUdqIdChange(): Promise<void> {
        this.clearFieldDetails();
        this.validate();

        if (this.sourceUdqId && this.sourceUdqId.length === GUID_LENGTH) {
            this.lookupFieldsLoading = true;

            try {
                const response = await this.tenantService.getQueryColumns(this.moduleId, this.sourceUdqId);
                this.sourceUdqModuleId = response.ModuleId;
                this.lookupFields = response.ReturnFields.map(field => ({
                    name: field.Alias,
                    dataType: field.FieldType,
                    mappings: this.generateUdqFieldMappings(field)
                 }));
            }
            catch (error) {
                switch (error.status) {
                    case 404:
                        this.showSnackBarMessage('Query does not exist', true);
                        break;

                    default:
                        this.showSnackBarMessage('Invalid Query Id', true);
                        break;
                }
            }
            finally {
                this.lookupFieldsLoading = false;
                this.validate();
            }
        }
    }

    generateUdqFieldMappings(dataField: IModuleReturnFieldConfig): BasicControlFieldMapping[] {
        if(dataField.FieldType !== 'datetimepicker'){
            return [];
        }

        return [{
            controlPropertyName: 'Date1',
            modelPropertyName: dataField.Alias
        }];
    }

    clearFieldDetails(): void {
        this.lookupFieldsLoading = false;
        this.lookupFields = null;
        this.primaryField = null;
        this.previewFields = [];
        this.autoFillFieldMappings = [];
        this.enabledWhenField = null;
        this.enabledWhenValue = null;
        this.sourceUdqModuleId = null;
    }

    onPrimaryFieldChange(): void {
        this.validate();
    }

    addNewPreviewField(): void {
        this.previewFields.push(this.createPreviewField());
    }

    createPreviewField(): IPreviewField {
        return {
            label: '',
            lookupField: null
        };
    }

    removePreviewField(index: number): void {
        this.previewFields.splice(index, 1);
    }

    clearEnabledWhen() {
        this.enabledWhenField = null;
        this.enabledWhenValue = '';
    }

    goBack(): void {
        const queryParameters = this.activatedRoute.snapshot.queryParams;
        this.router.navigate(
            ['../../steps/step/fields-container/parent-fields'],
            {
                relativeTo: this.activatedRoute,
                queryParams: {
                    stepId: queryParameters.stepId,
                    getMultiFields: queryParameters.getMultiFields
                }
            }
        );
    }

    async onSave(): Promise<void> {
        if (!this.validate()) {
            return;
        }

        this.field.AutoFillDefinition = this.mapToAutoFillDefinition();

        if (!this.field.IsCustomField) {
            this.field.Modified = true;
        }

        await this.saveMappedFieldsForServerSideLookup();
        this.hideLookupIfServerSide();

        Promise.all([
            this.moduleService
                .patchField(
                    this.tenantService.selectedTenant,
                    this.moduleId,
                    this.stepId,
                    this.field)
                .toPromise(),
            this.moduleService
                .updateField(
                    this.field,
                    this.tenantService.selectedTenant,
                    this.moduleId,
                    this.version,
                    this.stepId)
                .toPromise()
        ])
        .then(() => this.showSnackBarMessage('Configuration Saved'))
        .catch((error) => {
            this.showErrorSavingSnackBar(error);
        });
    }

    autoFillMappingsChanged($event): void {
        this.autoFillFieldMappings = $event;
    }

    setProcessingType($event: ProcessType): void {
        this.processType = $event;
    }

    get isClientSideProcessing(): boolean {
        return this.processType === ProcessType.client;
    }

    get isServerSideProcessing(): boolean {
        return this.processType === ProcessType.server;
    }

    private fetchFeeds(): Promise<IFeedConfiguration[]> {
        const tenantName = this.tenantService.selectedTenant.Value;
        return this.feedService.getFeedConfigurations(tenantName).toPromise();
    }

    private async loadFieldAutoFillDefinition(autoFillDefinition: AutoFillDefinition): Promise<void> {
        if (autoFillDefinition.LookupTableName) {
            this.sourceType = SourceType.Feed;
            this.sourceFeed = this.feeds.filter(feed => feed.Name === autoFillDefinition.LookupTableName)[0];
            this.onSourceFeedChange();
        }
        else if (autoFillDefinition.QueryModuleId && autoFillDefinition.QueryId) {
            this.sourceType = SourceType.Udq;
            this.sourceUdqId = autoFillDefinition.QueryId;
            await this.onSourceUdqIdChange();
        }

        this.processType = autoFillDefinition.ProcessType ?? ProcessType.client;

        if (!this.sourceType || (this.sourceType === SourceType.Feed && !this.sourceFeed)
            || (this.sourceType === SourceType.Udq && !this.sourceUdqId) || !this.lookupFields) {
            return null;
        }

        if (autoFillDefinition.DefaultName) {
            this.primaryField = this.lookupFields.filter(lookupField => lookupField.name === autoFillDefinition.DefaultName)[0];
        }

        this.previewFields = [];
        for (const fieldLabel of Object.keys(autoFillDefinition.PreviewFields)) {
            if (!autoFillDefinition.PreviewFields[fieldLabel]) {
                continue;
            }

            this.previewFields.push(
                {
                    label: fieldLabel,
                    lookupField: this.lookupFields.filter(lField => lField.name === autoFillDefinition.PreviewFields[fieldLabel])[0]
                }
            );
        }

        this.autoFillFieldMappings = [];

        for (const fieldName of Object.keys(autoFillDefinition.EnabledWhen)) {
            this.enabledWhenField = this.otherFields.filter(otherField => otherField.FieldId === fieldName)[0];
            this.enabledWhenValue = autoFillDefinition.EnabledWhen[fieldName];
            break;
        }

        this.isEnabledOffline = autoFillDefinition.IsEnabledOffline;
        this.matchType = autoFillDefinition.MatchType;
        this.failValidationOnFailedLookup = autoFillDefinition.FailValidationOnFailedLookup;
        this.publishAnywayOnFailedLookup = autoFillDefinition.PublishAnywayOnFailedLookup;
        this.allowMappingAfterFirstSave = autoFillDefinition.AllowMappingAfterFirstSave;
        if (autoFillDefinition.DefaultField) {
            this.defaultField = autoFillDefinition.DefaultField;
        }
    }

    private async saveMappedFieldsForServerSideLookup(): Promise<void | [{}, Field[]]> {
        if (this.isClientSideProcessing) {
            return;
        }

        const patches: Patch[] = [];

        for (const fieldMapping of this.autoFillFieldMappings) {
            const field = fieldMapping.field;
            const valueIsProperty = this.createValueIsPropertyForField(field);

            if (!this.fieldContainsValueIsProperty(field, valueIsProperty)) {
                field.VisibleWhenRules.push(valueIsProperty);
                patches.push(this.createPatchForValueIs(valueIsProperty, field.FieldId));
            }

            if (field.IsFullControlButReadOnly !== true) {
                field.IsFullControlButReadOnly = true;
                patches.push(this.createForReadOnlyPatch(field));
            }
        }

        if (patches.length === 0) {
            return;
        }

        const autoFillFields = this.autoFillFieldMappings.map(({ field: Field }) => Field);

        return Promise.all([
            this.moduleService
            .applyPatch(
                this.tenantService.selectedTenant,
                this.moduleId,
                patches),
            this.moduleService
            .updateFields(
                autoFillFields,
                this.tenantService.selectedTenant,
                this.moduleId,
                this.version,
                this.stepId)
            .toPromise()
        ]).catch((error) => {
            this.showErrorSavingSnackBar(error);
        });
    }

    private showErrorSavingSnackBar(error: any): void {
        console.error(error);
        this.showSnackBarMessage('Error Saving', true);
    }

    private hideLookupIfServerSide(): void {
        if (!this.isClientSideProcessing && this.defaultField !== DefaultField.None) {
            this.field.IsAlwaysHidden = true;
        }
    }

    private fieldContainsValueIsProperty(field: Field, valueIsProperty: ValueIsProperty): boolean {
        const visibilityRules = field.VisibleWhenRules;
        return visibilityRules.some(rule => rule.PropertyName === valueIsProperty.PropertyName
            && rule.ValueIs.Identifier === valueIsProperty.ValueIs.Identifier);
    }

    private createValueIsPropertyForField(field: Field): ValueIsProperty {
        return {
            PropertyName: this.getPropertyNameForField(field),
            ValueIs: {
                Identifier: 'defined',
                Value: null
            }
        };
    }

    private createForReadOnlyPatch(field: Field): Patch {
        return {
            Operation: 'Upsert',
            Parameters: {
                Value: true,
                PropertyName: 'IsFullControlButReadOnly'
            },
            PropertyPaths: [
                [
                    ...this.moduleService.fieldsPath,
                    field.FieldId,
                    'Control',
                    'Properties'
                ]
            ]
        };
    }

    private getPropertyNameForField(field: Field): string {

        if (field.FieldUniqueId === 'datetimepicker') {
            return field.FieldId + 'Date';
        }
        return field.FieldId;
    }

    private createPatchForValueIs(valueIsProperty: ValueIsProperty, fieldId: string): Patch {
        return {
            Operation: 'Upsert',
            Parameters: {
                Value: [ valueIsProperty ],
                PropertyName: 'VisibilityRules'

            },
            PropertyPaths: [
                [
                    ...this.moduleService.fieldsPath,
                    fieldId,
                    'Control',
                    'Properties'
                ]
            ]
        };
    }

    private getMatchType(): string {
        return this.matchType || MatchType.Fuzzy;
    }

    private mapToAutoFillDefinition(): AutoFillDefinition {
        const autoFillDefinitionBuilder = new AutoFillDefinitionBuilder();

        switch (this.sourceType) {
            case SourceType.Feed:
                autoFillDefinitionBuilder.setProcessingType(this.processType)
                .addFeedLookupData(this.sourceFeed.Name, this.getMatchType())
                .addValidationHandlingOptions(
                    this.failValidationOnFailedLookup,
                    this.publishAnywayOnFailedLookup,
                    this.allowMappingAfterFirstSave);
                break;

            case SourceType.Udq:
                autoFillDefinitionBuilder.addQueryIdentifier(this.sourceUdqModuleId, this.sourceUdqId);
                break;

            default:
                throw new Error('Invalid source type');
        }

        autoFillDefinitionBuilder.addDefaultName(this.primaryField.name)
        .addPreviewFields(this.previewFields)
        .addControlValueMappings(this.autoFillFieldMappings, this.lookupFields, this.sourceType, this.processType)
        .enabledWhen(this.enabledWhenField, this.enabledWhenValue)
        .isEnabledOffline(this.isEnabledOffline)
        .addDefaultField(this.defaultField);

        return autoFillDefinitionBuilder.build();
    }

    private validate(): boolean {
        return this.valid = !!this.lookupFields && !!this.primaryField;
    }

    private showSnackBarMessage(message: string, error: boolean = false) {
        const config = new MatSnackBarConfig();
        config.duration = 10000;
        config.politeness = error ? 'assertive' : 'polite';
        this.snackBar.open(message, null, config);
    }
}
